import type { VoiceMode } from '@cerebro-diem/core';
import { MAX_RECORDING_MS, RECORDING_LIMIT_NOTE, SILENCE_NOTE, SILENCE_STOP_MS, silenceWatch } from '@cerebro-diem/core';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { KeyboardEvent as ReactKeyboardEvent, TouchEvent as ReactTouchEvent } from 'react';
import { getSession } from './supabase';
import { transcribeRecording } from './transcription';
import type { CaptureRecording } from './captureStorage';
import { errorMessage } from './errors';
import { requireRawRecording, appendRawRecordingChunk, keepRawRecording, listRawRecordings, removeRawRecording, type RawRecording } from './rawRecordings';
import { useAuthStore } from '../stores/authStore';

const MIN_AUDIO_BYTES = 1000;
/** How often the microphone's level is read for the silence stop. */
const METER_MS = 250;

/** The loudest moment in what the analyser holds now, in dB below full scale. */
function peakDbfs(analyser: AnalyserNode, buffer: Float32Array<ArrayBuffer>): number {
  analyser.getFloatTimeDomainData(buffer);
  let peak = 0;
  for (const sample of buffer) peak = Math.max(peak, Math.abs(sample));
  return peak > 0 ? 20 * Math.log10(peak) : -160;
}

/** What a recording reports back. Each consumer decides what the words become and how a failure reads. */
interface RecordingEvents {
  /** A recording was asked for: clear what the last one left on screen. */
  onStart?: () => void;
  /** Stable destination for owner-specific raw-audio recovery. */
  recoveryKey?: string;
  /** The raw source has been physically removed after downstream accepted custody. */
  onCustodyReleased?: (recordingId: string, ownerId: string) => void | Promise<void>;
  /** Why nothing came of the recording, as a sentence ("No speech was recognized; try again or type it."). */
  onError: (reason: string) => void;
  /** The recording stopped by itself (at its time limit, or after silence); what to say about it. It is still transcribed. */
  onLimit?: (note: string) => void;
}

export type VoiceRecorderEvents = RecordingEvents & (
  | { onRecording: (recording: CaptureRecording, ownerId: string, recordingId: string) => boolean | Promise<boolean>; onTranscript?: never }
  | { onTranscript: (transcript: string, ownerId: string, recordingId: string) => boolean | Promise<boolean>; onRecording?: never }
);

function microphoneReason(err: unknown): string {
  if (err instanceof DOMException && err.name === 'NotAllowedError') {
    return 'Microphone access is denied; allow it and try again.';
  }
  return `Could not use the microphone: ${errorMessage(err)}`;
}

const isPressKey = (key: string) => key === ' ' || key === 'Enter';

/** The words a record button says, by the person's tap-or-hold setting (CER-175). */
export function recorderWords(mode: VoiceMode) {
  return mode === 'tap_to_start'
    ? { verb: 'Tap', stop: 'Tap to stop', recording: 'Recording… tap to stop', tooShort: 'record a little longer' }
    : { verb: 'Hold', stop: 'Release to stop', recording: 'Recording… release to stop', tooShort: 'hold the button longer' };
}

/** How long a recording may run and how much quiet ends it, and what is said when either does. */
export interface RecordingLimits {
  maxMs: number;
  silenceMs: number;
  limitNote: string;
  silenceNote: string;
}

/** A capture or a dictated field: ten minutes, and thirty seconds of quiet. */
export const CAPTURE_LIMITS: RecordingLimits = {
  maxMs: MAX_RECORDING_MS,
  silenceMs: SILENCE_STOP_MS,
  limitNote: RECORDING_LIMIT_NOTE,
  silenceNote: SILENCE_NOTE,
};

/**
 * Record, then transcribe: the microphone through MediaRecorder, then the
 * `transcribe-audio` edge function. The one recorder the capture box and
 * every dictated field share. `mode` is the person's setting: hold to record
 * and release to stop, or tap to start and tap again to stop (CER-175).
 * `pressHandlers` go on the record button (mouse, touch, and Space or Enter).
 */
export function useVoiceRecorder(events: VoiceRecorderEvents, mode: VoiceMode = 'push_to_talk', limits: RecordingLimits = CAPTURE_LIMITS) {
  // Read when a recording ends, so the latest callbacks run and not the ones from when it began.
  const eventsRef = useRef(events);
  eventsRef.current = events;
  // Read when a recording starts: how long it may run and how much quiet ends it.
  const limitsRef = useRef(limits);
  limitsRef.current = limits;
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const retained = useRef(new Map<string, RawRecording>());
  const [recoveryCount, setRecoveryCount] = useState(0);
  const recoveryOwner = useAuthStore(state => state.user?.id ?? null);
  const purpose = events.onRecording ? 'capture' : events.recoveryKey ?? 'dictation';
  const submittingRef = useRef(false);
  useEffect(() => {
    if (!recoveryOwner) { setRecoveryCount(0); return; }
    void listRawRecordings(recoveryOwner, purpose).then(records => {
      if (useAuthStore.getState().user?.id !== recoveryOwner) return;
      for (const record of records) if (!retained.current.has(record.id)) retained.current.set(record.id, record);
      setRecoveryCount([...retained.current.values()].filter(r => r.ownerId === recoveryOwner && r.purpose === purpose).length);
    }, () => eventsRef.current.onError('Raw recording storage could not be read. Existing audio remains quarantined and untouched.'));
  }, [recoveryOwner, purpose]);
  const startSequence = useRef(0);
  const isRecordingRef = useRef(false);
  /** Stops a recording that reaches MAX_RECORDING_MS, as letting go or a second tap would (CER-83). */
  const limitRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** Reads the microphone's level while recording; thirty seconds of silence stops it, kept. */
  const meterRef = useRef<{ context: AudioContext; timer: ReturnType<typeof setInterval> } | null>(null);

  const stopMeter = () => {
    if (!meterRef.current) return;
    clearInterval(meterRef.current.timer);
    void meterRef.current.context.close().catch(() => eventsRef.current.onError('The microphone level monitor could not close. Reload this page before recording again.'));
    meterRef.current = null;
  };

  const startMeter = (stream: MediaStream) => {
    if (typeof AudioContext === 'undefined') {
      throw new Error('This browser cannot measure the microphone level. Update the browser before recording.');
    }
    const context = new AudioContext();
    let analyser: AnalyserNode;
    try {
      analyser = context.createAnalyser();
    analyser.fftSize = 2048;
      context.createMediaStreamSource(stream).connect(analyser);
    } catch (error) {
      void context.close().catch(() => eventsRef.current.onError('The microphone level monitor could not close. Reload this page before recording again.'));
      throw error;
    }
    const buffer = new Float32Array(analyser.fftSize);
    const heard = silenceWatch(limitsRef.current.silenceMs);
    const startedAt = Date.now();
    const timer = setInterval(() => {
      if (!isRecordingRef.current) return;
      if (heard(Date.now() - startedAt, peakDbfs(analyser, buffer))) {
        eventsRef.current.onLimit?.(limitsRef.current.silenceNote);
        stopRef.current();
      }
    }, METER_MS);
    meterRef.current = { context, timer };
  };

  const transcribe = async (record: RawRecording) => {
    if (submittingRef.current) return;
    submittingRef.current = true;
    setIsTranscribing(true);
    try {
      if (!navigator.locks) throw new Error('This browser cannot coordinate raw recording custody.');
      await navigator.locks.request(`cerebro-raw-recording:${record.id}`, async () => {
      const original = await requireRawRecording(record.id, record.ownerId);
      const { data: { session } } = await getSession();
      if (!session || session.user.id !== record.ownerId || useAuthStore.getState().user?.id !== record.ownerId) throw new Error('Sign in as the recording owner to retry.');
      await keepRawRecording(record);
      const capture = eventsRef.current.onRecording;
      if (capture) {
        if (await capture({ blob: record.blob, durationMs: record.durationMs }, record.ownerId, record.id)) retained.current.delete(record.id);
        return;
      }
      const transcript = original.transcript ?? record.transcript ?? await transcribeRecording(record.blob, record.ownerId);
      const transcribed = { ...record, transcript };
      await keepRawRecording(transcribed);
      retained.current.set(record.id, transcribed);
      if (useAuthStore.getState().user?.id !== record.ownerId) throw new Error('The signed-in account changed. The recording remains with its original owner.');
      const receive = eventsRef.current.onTranscript;
      if (!receive) throw new Error('The recording consumer changed before transcription completed');
      const accepted = await receive(transcript, record.ownerId, record.id);
      if (accepted) {
        await removeRawRecording(record.id, record.ownerId);
        retained.current.delete(record.id);
        try { await eventsRef.current.onCustodyReleased?.(record.id, record.ownerId); }
        catch { eventsRef.current.onError('Audio was handed over and removed, but its transfer receipt could not be cleaned up. Retry storage.'); }
      }
      });
    } catch (err) {
      eventsRef.current.onError(`Not transcribed or handed over yet: ${errorMessage(err)} The recording is kept for its owner; retry. If storage is unavailable, keep this page open.`);
    } finally {
      submittingRef.current = false;
      setIsTranscribing(false);
      const owner = useAuthStore.getState().user?.id;
      setRecoveryCount([...retained.current.values()].filter(r => r.ownerId === owner && r.purpose === purpose).length);
    }
  };
  const retry = async () => {
    const owner = useAuthStore.getState().user?.id;
    for (const record of retained.current.values()) {
      if (record.ownerId === owner && record.purpose === purpose) {await transcribe(record);}
    }
  };

  const start = async () => {
    if (isRecordingRef.current || submittingRef.current) return;
    const startAttempt = ++startSequence.current;
    const wanted = () => startSequence.current === startAttempt && isRecordingRef.current;
    isRecordingRef.current = true;
    eventsRef.current.onStart?.();

    let stage: 'auth' | 'microphone' = 'auth';
    let stream: MediaStream | undefined;
    try {
      const { data: { session } } = await getSession();
      if (!wanted()) return;
      if (!session) throw new Error('Sign in before recording.');
      const ownerId = session.user.id;
      stage = 'microphone';
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      if (!wanted()) { stream.getTracks().forEach(track => track.stop()); return; }

      stage = 'auth';
      const { data: { session: currentSession } } = await getSession();
      if (!wanted()) { stream.getTracks().forEach(track => track.stop()); return; }
      if (!currentSession || currentSession.user.id !== ownerId) throw new Error('The signed-in account changed before recording began.');
      stage = 'microphone';
      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus'
        : MediaRecorder.isTypeSupported('audio/webm')
          ? 'audio/webm'
          : 'audio/mp4';

      // Speech quality, as the phone records it: ten minutes is a few megabytes, not tens.
      const mediaRecorder = new MediaRecorder(stream, { mimeType, audioBitsPerSecond: 32_000 });
      const chunks: Blob[] = [];
      const raw = { id: crypto.randomUUID(), ownerId, purpose, createdAt: new Date().toISOString(), blob: new Blob([], { type: mimeType }), durationMs: 0 };
      await keepRawRecording(raw);
      if (!wanted()) { stream.getTracks().forEach(track => track.stop()); await removeRawRecording(raw.id, ownerId); return; }
      mediaRecorderRef.current = mediaRecorder;
      let writes = Promise.resolve();
      let storageFailed = false;

      const startedAt = Date.now();
      const snapshot = () => ({ ...raw, blob: new Blob(chunks, { type: mimeType }), durationMs: Math.max(0, Date.now() - startedAt) });
      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size === 0) return;
        chunks.push(e.data);
        const record = snapshot();
        retained.current.set(record.id, record);
        const index = chunks.length - 1;
        writes = writes.then(async () => {
          try { await appendRawRecordingChunk(record.id, record.ownerId, index, e.data, record.durationMs); }
          catch {
            storageFailed = true;
            eventsRef.current.onError('Not saved on this device: raw recording storage failed. Keep this page open and retry the retained audio; transcription has not started.');
            stopRef.current();
          }
        });
      };
      mediaRecorder.onstop = async () => {
        try {
        stopMeter();
        stream!.getTracks().forEach((track) => track.stop());

        // A recording with no words in it is thrown away, not kept for retry: a kept scrap would only fail again
        // and would hold the capture box's Save and microphone until it was dealt with.
        const discardScrap = async (said: string) => {
          await writes;
          retained.current.delete(raw.id);
          try {
            await removeRawRecording(raw.id, ownerId);
            eventsRef.current.onError(said);
          } catch (error) {
            eventsRef.current.onError(`${said} Its empty leftover could not be removed from this device: ${error instanceof Error ? error.message : 'unknown error'}`);
          }
          setRecoveryCount([...retained.current.values()].filter(r => r.ownerId === useAuthStore.getState().user?.id && r.purpose === purpose).length);
        };
        if (chunks.length === 0) {
          await discardScrap('No audio was recorded; try again.');
          return;
        }
        const audioBlob = new Blob(chunks, { type: mimeType });
        if (audioBlob.size < MIN_AUDIO_BYTES) {
          await discardScrap(`The recording was too short; ${recorderWords(mode).tooShort}.`);
          return;
        }
          await writes;
          const record = snapshot();
          retained.current.set(record.id, record);
          setRecoveryCount([...retained.current.values()].filter(r => r.ownerId === useAuthStore.getState().user?.id && r.purpose === purpose).length);
          if (storageFailed) return;
          await transcribe(record);
        } catch { eventsRef.current.onError('The recording handoff failed. Its retained local audio stays with the original account; keep this page open and retry.'); }
        finally { setRecoveryCount([...retained.current.values()].filter(record => record.ownerId === useAuthStore.getState().user?.id && record.purpose === purpose).length); }
      };

      startMeter(stream);
      mediaRecorder.start(100);
      setIsRecording(true);
      limitRef.current = setTimeout(() => {
        if (!isRecordingRef.current) return;
        eventsRef.current.onLimit?.(limitsRef.current.limitNote);
        stopRef.current();
      }, limitsRef.current.maxMs);
    } catch (err) {
      if (startSequence.current !== startAttempt) {
        stream?.getTracks().forEach(track => track.stop());
        eventsRef.current.onError('Recording start was cancelled, but session or storage cleanup did not finish. Retry storage before recording again.');
        return;
      }
      isRecordingRef.current = false;
      setIsRecording(false);
      stopMeter();
      if (mediaRecorderRef.current) {
        mediaRecorderRef.current.onstop = null;
        if (mediaRecorderRef.current.state !== 'inactive') mediaRecorderRef.current.stop();
        mediaRecorderRef.current = null;
      }
      stream?.getTracks().forEach(track => track.stop());
      eventsRef.current.onError(stage === 'auth' ? 'The signed-in session could not be confirmed. Sign in as the recording owner and try again.' : microphoneReason(err));
    }
  };

  const stop = useCallback(() => {
    startSequence.current++;
    if (limitRef.current !== null) {
      clearTimeout(limitRef.current);
      limitRef.current = null;
    }
    stopMeter();
    if (!isRecordingRef.current) return;
    isRecordingRef.current = false;
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    setIsRecording(false);
  }, []);
  const stopRef = useRef(stop);
  stopRef.current = stop;

  // Holding: stop even if the finger slides off the button before lifting. Tapping has no such end.
  useEffect(() => {
    if (mode === 'tap_to_start') return;
    document.addEventListener('touchend', stop);
    document.addEventListener('touchcancel', stop);
    return () => {
      document.removeEventListener('touchend', stop);
      document.removeEventListener('touchcancel', stop);
    };
  }, [stop, mode]);

  useEffect(() => {
    const finish = () => stopRef.current();
    window.addEventListener('pagehide', finish);
    return () => { window.removeEventListener('pagehide', finish); finish(); };
  }, []);

  const holdHandlers = {
    onMouseDown: () => void start(),
    onMouseUp: stop,
    // Touch handlers: more reliable than mouse events on phones.
    onTouchStart: (e: ReactTouchEvent) => {
      e.preventDefault(); // keep the matching mouse events from also firing
      void start();
    },
    onTouchEnd: (e: ReactTouchEvent) => {
      e.preventDefault();
      stop();
    },
    onTouchCancel: stop,
    onKeyDown: (e: ReactKeyboardEvent) => {
      if (!isPressKey(e.key)) return;
      e.preventDefault();
      if (!e.repeat) void start();
    },
    onKeyUp: (e: ReactKeyboardEvent) => {
      if (!isPressKey(e.key)) return;
      e.preventDefault();
      stop();
    },
  };

  // Tapping: one press starts, the next stops. A click covers mouse, touch and the keyboard.
  const tapHandlers = {
    onClick: () => {
      if (isRecordingRef.current) stop();
      else void start();
    },
  };

  const recoveryRecording = [...retained.current.values()].find(record => record.ownerId === recoveryOwner && record.purpose === purpose && record.blob.size > 0) ?? null;
  return { isRecording, isTranscribing, recoveryCount, recoveryRecording, retry, pressHandlers: mode === 'tap_to_start' ? tapHandlers : holdHandlers, words: recorderWords(mode) };
}

/**
 * `value` with `transcript` added. At a cursor or selection (`at`), the words
 * replace the selection, spaced from the text on either side; with no cursor
 * placed, they start a new line after what is there.
 */
export function withTranscript(value: string, transcript: string, at: { start: number; end: number } | null): { text: string; cursor: number } {
  if (!at) {
    const separator = value && !value.endsWith('\n') ? '\n' : '';
    const text = `${value}${separator}${transcript}`;
    return { text, cursor: text.length };
  }
  const before = value.slice(0, at.start);
  const after = value.slice(at.end);
  const lead = before && !/\s$/.test(before) ? ' ' : '';
  const trail = after && !/^\s/.test(after) ? ' ' : '';
  const inserted = `${lead}${transcript}${trail}`;
  return { text: `${before}${inserted}${after}`, cursor: before.length + lead.length + transcript.length };
}
